import React, { useState, useEffect, useRef } from 'react';
import { UserPlus, LogIn, Camera, Search, Check, ArrowLeftToLine, ArrowRightFromLine, Building2, Phone, IdCard, RefreshCw } from 'lucide-react';
import { compressImage } from '../lib/imageCompression';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Stage = {
  ENTRY: 'entry',           // first screen: lookup or register
  REGISTER: 'register',
  OTP: 'otp',
  CHOOSE_STAFF: 'choose_staff',
  ACTIVE: 'active'          // shows code + status
};

export default function VisitorPage() {
  const [stage, setStage] = useState(Stage.CHOOSE_STAFF);
  const [mobile, setMobile] = useState('');
  const [visitor, setVisitor] = useState(null);

  // register form
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [designation, setDesignation] = useState('');
  const [photo, setPhoto] = useState(null);
  const fileRef = useRef(null);

  // otp
  const [otp, setOtp] = useState('');

  // choose staff
  const [staffList, setStaffList] = useState([]);
  const [staffQuery, setStaffQuery] = useState('');
  const [chosenStaff, setChosenStaff] = useState(null);
  const [purposeOption, setPurposeOption] = useState('');
  const [purposeOther, setPurposeOther] = useState('');

  // active
  const [activeVisit, setActiveVisit] = useState(null);
  const [busy, setBusy] = useState(false);

  // out remarks
  const [showOutRemarks, setShowOutRemarks] = useState(false);
  const [outRemarks, setOutRemarks] = useState('');

  const PURPOSE_OPTIONS = [
    'New Marketing',
    'Sample giving',
    'Bill',
    'Statement',
    'Payment followup',
    'Cheque receive',
    'Delivery',
    'Status discussion',
    'Surplus sales enquiry',
    'Surplus work'
  ];

  // Load active visit if visitor known
  useEffect(() => {
    if (!visitor?.id) return;
    fetchActive();
    const t = setInterval(fetchActive, 5000);
    return () => clearInterval(t);
  }, [visitor]);

  // Load staff list immediately so the staff-selection screen is ready up-front.
  useEffect(() => { loadStaff(); }, []);

  const fetchActive = async () => {
    if (!visitor?.id) return;
    try {
      const r = await fetch(`${API_URL}/api/public/visitors/${visitor.id}/active-visit`);
      const d = await r.json();
      if (d && d.id) {
        setActiveVisit(d);
        setStage(Stage.ACTIVE);
      } else {
        setActiveVisit(null);
        if (stage === Stage.ACTIVE) setStage(Stage.CHOOSE_STAFF);
      }
    } catch {}
  };

  const handleLookup = async (e) => {
    e?.preventDefault();
    if (!chosenStaff) { alert('Please pick a staff member first'); setStage(Stage.CHOOSE_STAFF); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/public/visitors/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile })
      });
      const d = await r.json();
      if (d.found) {
        setVisitor(d.visitor);
        // Visitor already verified previously — go straight to entry-OTP issuance.
        await startVisitForVisitor(d.visitor.id);
      } else {
        setStage(Stage.REGISTER);
      }
    } finally { setBusy(false); }
  };

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const compressed = await compressImage(f);
    setPhoto(compressed);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!photo) { alert('Please take a selfie'); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/public/visitors/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, mobile, company, designation, photo_base64: photo })
      });
      const d = await r.json();
      if (r.ok) {
        if (d.already_registered) {
          // Should not happen since we checked, but fallback
          await handleLookup();
        } else {
          setVisitor({ id: d.visitor_id, name, mobile, company, designation, photo_base64: photo });
          setStage(Stage.OTP);
        }
      } else {
        alert(d.detail || 'Failed');
      }
    } finally { setBusy(false); }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/public/visitors/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_id: visitor.id, otp })
      });
      const d = await r.json();
      if (r.ok) {
        setVisitor(d.visitor);
        setOtp('');
        // Verified — directly issue the entry OTP (staff was already chosen earlier).
        await startVisitForVisitor(d.visitor.id);
      } else {
        alert(d.detail || 'Failed');
      }
    } finally { setBusy(false); }
  };

  const loadStaff = async () => {
    const r = await fetch(`${API_URL}/api/public/staff-list`);
    const d = await r.json();
    setStaffList(d || []);
  };

  const handleStartVisit = () => {
    // Step 1: pick staff + purpose. Now move on to the mobile/registration step.
    if (!chosenStaff) { alert('Please pick a staff member'); return; }
    if (!purposeOption) { alert('Please select a visit purpose'); return; }
    if (purposeOption === 'Others' && !purposeOther.trim()) { alert('Please specify the purpose'); return; }
    setStage(Stage.ENTRY);
  };

  // Actually create the visit on the backend once the visitor is authenticated
  // (either looked up or newly OTP-verified). The chosen staff + purpose were
  // captured in step 1.
  const startVisitForVisitor = async (visitorId) => {
    if (!chosenStaff) { alert('Please pick a staff member'); setStage(Stage.CHOOSE_STAFF); return; }
    const finalPurpose = purposeOption === 'Others' ? `Others: ${purposeOther.trim()}` : purposeOption;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/public/visitors/start-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitor_id: visitorId, staff_id: chosenStaff.id, purpose: finalPurpose })
      });
      const d = await r.json();
      if (r.ok) {
        setActiveVisit(d.visit);
        setStage(Stage.ACTIVE);
      } else {
        alert(d.detail || 'Failed');
      }
    } finally { setBusy(false); }
  };

  const handleEndVisit = async () => {
    if (!activeVisit) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/public/visitors/end-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visit_id: activeVisit.id, visitor_remarks: outRemarks })
      });
      if (r.ok) {
        setShowOutRemarks(false);
        setOutRemarks('');
        await fetchActive();
      } else { const d = await r.json(); alert(d.detail || 'Failed'); }
    } finally { setBusy(false); }
  };

  const handleResendExitOtp = async () => {
    if (!activeVisit) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/public/visitors/${activeVisit.id}/resend-exit-otp`, { method: 'POST' });
      const d = await r.json();
      if (r.ok) {
        alert('Exit OTP re-sent on WhatsApp.');
      } else {
        alert(d.detail || 'Could not re-send. Please try again.');
      }
    } finally { setBusy(false); }
  };

  const filteredStaff = staffList.filter(s =>
    !staffQuery || s.name.toLowerCase().includes(staffQuery.toLowerCase()) ||
    (s.staff_id || '').toLowerCase().includes(staffQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img
            src="https://customer-assets.emergentagent.com/job_carloo-tex/artifacts/apbouvvy_WhatsApp%20Image%202026-04-14%20at%204.55.05%20PM.jpeg"
            alt="Carloo"
            className="h-16 mx-auto mb-3"
          />
          <h1 className="text-2xl font-bold text-gray-900">Visitor Self-Service</h1>
          <p className="text-gray-600 text-sm">Sign in to meet our team</p>
        </div>

        {/* ENTRY: mobile lookup */}
        {stage === Stage.ENTRY && (
          <div className="bg-white rounded-2xl shadow-xl p-6" data-testid="visitor-entry">
            {chosenStaff && (
              <div className="mb-3 text-center text-xs text-gray-500" data-testid="visitor-entry-meeting-banner">
                Meeting <strong className="text-gray-700">{chosenStaff.name}</strong>
                <button onClick={() => setStage(Stage.CHOOSE_STAFF)} className="ml-2 underline text-amber-700">change</button>
              </div>
            )}
            <form onSubmit={handleLookup} className="space-y-4">
              <h2 className="text-lg font-semibold mb-2">Enter your mobile number</h2>
              <input
                type="tel" inputMode="numeric"
                placeholder="10-digit mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-lg focus:border-amber-400 outline-none"
                required
                data-testid="visitor-mobile-input"
              />
              <button type="submit" disabled={busy} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 rounded-xl flex items-center justify-center space-x-2" data-testid="visitor-lookup-btn">
                <LogIn size={20} /><span>{busy ? 'Checking...' : 'Continue'}</span>
              </button>
            </form>
            <div className="mt-4 pt-4 border-t text-center">
              <p className="text-sm text-gray-500 mb-2">First time visiting us?</p>
              <button onClick={() => setStage(Stage.REGISTER)} className="text-amber-700 font-medium flex items-center justify-center space-x-1 mx-auto" data-testid="visitor-register-link">
                <UserPlus size={16} /><span>Register as Guest</span>
              </button>
            </div>
          </div>
        )}

        {/* REGISTER */}
        {stage === Stage.REGISTER && (
          <div className="bg-white rounded-2xl shadow-xl p-6" data-testid="visitor-register">
            <h2 className="text-lg font-semibold mb-4">Guest Registration</h2>
            <form onSubmit={handleRegister} className="space-y-3">
              <input type="text" placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl outline-none focus:border-amber-400" required data-testid="visitor-name-input" />
              <input type="tel" inputMode="numeric" placeholder="Mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl outline-none focus:border-amber-400" required data-testid="visitor-reg-mobile" />
              <input type="text" placeholder="Company" value={company} onChange={(e) => setCompany(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl outline-none focus:border-amber-400" required data-testid="visitor-company-input" />
              <input type="text" placeholder="Designation" value={designation} onChange={(e) => setDesignation(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl outline-none focus:border-amber-400" required data-testid="visitor-designation-input" />
              
              <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={handlePhoto} className="hidden" data-testid="visitor-photo-input" />
              <button type="button" onClick={() => fileRef.current?.click()} className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center space-x-2" data-testid="visitor-photo-btn">
                <Camera size={18} /><span>{photo ? 'Selfie Captured ✓' : 'Take Selfie'}</span>
              </button>
              {photo && <img src={photo} alt="selfie" className="mx-auto max-h-32 rounded-lg" />}
              
              <button type="submit" disabled={busy} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-3 rounded-xl flex items-center justify-center space-x-2" data-testid="visitor-register-submit">
                <UserPlus size={18} /><span>{busy ? 'Sending OTP...' : 'Register & send OTP'}</span>
              </button>
              <button type="button" onClick={() => setStage(Stage.ENTRY)} className="w-full text-sm text-gray-500">Cancel</button>
            </form>
          </div>
        )}

        {/* OTP */}
        {stage === Stage.OTP && (
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center" data-testid="visitor-otp">
            <h2 className="text-lg font-semibold mb-2">Verify your mobile</h2>
            <p className="text-sm text-gray-600 mb-4">A 4-digit OTP was sent on WhatsApp to <strong>{mobile}</strong></p>
            <form onSubmit={handleVerifyOtp} className="space-y-3">
              <input
                type="text" inputMode="numeric" maxLength={4}
                placeholder="••••"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl text-center text-3xl font-mono tracking-[1em] outline-none focus:border-amber-400"
                required
                data-testid="visitor-otp-input"
              />
              <button type="submit" disabled={busy || otp.length !== 4} className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-bold py-3 rounded-xl" data-testid="visitor-otp-submit">
                {busy ? 'Verifying...' : 'Verify OTP'}
              </button>
              <button type="button" onClick={() => setStage(Stage.REGISTER)} className="w-full text-sm text-gray-500">Edit details</button>
            </form>
          </div>
        )}

        {/* CHOOSE STAFF */}
        {stage === Stage.CHOOSE_STAFF && (
          <div className="bg-white rounded-2xl shadow-xl p-6" data-testid="visitor-choose-staff">
            {visitor && (
              <div className="flex items-center space-x-3 pb-3 mb-3 border-b">
                {visitor.photo_base64 && <img src={visitor.photo_base64} alt="" className="w-12 h-12 rounded-full object-cover" />}
                <div className="flex-1">
                  <p className="font-semibold">{visitor.name}</p>
                  <p className="text-xs text-gray-500">{visitor.company} • {visitor.designation}</p>
                </div>
              </div>
            )}
            <h2 className="text-lg font-semibold mb-3">Whom do you want to meet?</h2>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text" placeholder="Search staff by name or ID"
                value={staffQuery} onChange={(e) => setStaffQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 border-2 border-gray-200 rounded-xl outline-none focus:border-amber-400"
                data-testid="visitor-staff-search"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1 mb-3" data-testid="visitor-staff-list">
              {filteredStaff.map(s => (
                <button
                  key={s.id} type="button"
                  onClick={() => setChosenStaff(s)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${chosenStaff?.id === s.id ? 'bg-amber-100 border border-amber-400' : 'hover:bg-gray-50'}`}
                  data-testid={`visitor-staff-${s.id}`}
                >
                  <p className="font-medium">{s.name}</p>
                  <p className="text-xs text-gray-500">{s.staff_id} • {s.role}</p>
                </button>
              ))}
              {filteredStaff.length === 0 && <p className="text-center text-gray-400 py-6 text-sm">No staff matched</p>}
            </div>
            {chosenStaff && (
              <div className="space-y-2 mb-3" data-testid="visitor-purpose-section">
                <select
                  value={purposeOption}
                  onChange={(e) => setPurposeOption(e.target.value)}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl outline-none focus:border-amber-400 bg-white"
                  data-testid="visitor-purpose-select"
                  required
                >
                  <option value="">— Select Visit Purpose —</option>
                  {PURPOSE_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  <option value="Others">Others (specify)</option>
                </select>
                {purposeOption === 'Others' && (
                  <div>
                    <input
                      type="text" maxLength={20}
                      placeholder="Specify (max 20 chars)"
                      value={purposeOther}
                      onChange={(e) => setPurposeOther(e.target.value)}
                      className="w-full px-4 py-2.5 border-2 border-amber-300 rounded-xl outline-none focus:border-amber-500"
                      data-testid="visitor-purpose-other"
                    />
                    <p className="text-xs text-gray-500 mt-1 text-right">{purposeOther.length}/20</p>
                  </div>
                )}
              </div>
            )}
            <button
              onClick={handleStartVisit}
              disabled={busy || !chosenStaff}
              className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-bold py-3 rounded-xl flex items-center justify-center space-x-2"
              data-testid="visitor-start-visit-btn"
            >
              <ArrowLeftToLine size={18} /><span>{chosenStaff ? `Continue to meet ${chosenStaff.name}` : 'Pick a staff member'}</span>
            </button>
          </div>
        )}

        {/* ACTIVE */}
        {stage === Stage.ACTIVE && activeVisit && (
          <div className="bg-white rounded-2xl shadow-xl p-6 text-center" data-testid="visitor-active">
            {(() => {
              const isExitPhase = activeVisit.status === 'meeting_done' || activeVisit.status === 'pending_out';
              const otp = isExitPhase ? activeVisit.exit_otp : activeVisit.entry_otp;
              const otpLabel = isExitPhase ? 'Your EXIT OTP' : 'Your ENTRY OTP';
              const otpToneCls = isExitPhase
                ? 'bg-purple-50 border-2 border-purple-300'
                : activeVisit.status === 'pending_in'
                  ? 'bg-orange-50 border-2 border-orange-300'
                  : 'bg-blue-50 border-2 border-blue-300';
              const otpColor = isExitPhase ? 'text-purple-700' : 'text-amber-700';
              return (
                <div className={`mb-4 p-4 rounded-xl ${otpToneCls}`} data-testid="visitor-otp-card">
                  <p className="text-sm text-gray-600 mb-1">{otpLabel}</p>
                  {otp ? (
                    <p className={`text-5xl font-mono font-extrabold tracking-widest ${otpColor}`}
                       data-testid={isExitPhase ? 'visitor-exit-otp' : 'visitor-entry-otp'}>
                      {otp}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Awaiting OTP from host…</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Code: <span className="font-mono font-semibold tracking-wide" data-testid="visitor-entry-code">{activeVisit.entry_code}</span>
                  </p>
                  <p className="text-sm mt-3 font-medium leading-snug">
                    {activeVisit.status === 'pending_in' && '✍️ Write this OTP in the gate register and go inside.'}
                    {activeVisit.status === 'inside' && `✓ Inside — meeting with ${activeVisit.staff_name}.`}
                    {activeVisit.status === 'meeting_done' && '✍️ Meeting complete. Write this EXIT OTP at the gate register on your way out.'}
                    {activeVisit.status === 'pending_out' && '✍️ Show this OTP at the gate to exit.'}
                  </p>
                  {isExitPhase && otp && (
                    <button
                      onClick={handleResendExitOtp}
                      disabled={busy}
                      className="mt-2 text-xs text-purple-700 hover:text-purple-900 underline font-medium disabled:opacity-50"
                      data-testid="visitor-resend-exit-otp"
                    >
                      Didn't get the WhatsApp? Re-send Exit OTP
                    </button>
                  )}
                  {activeVisit.meeting_remarks && (
                    <p className="text-xs text-gray-600 mt-2">📝 {activeVisit.meeting_remarks}</p>
                  )}
                </div>
              );
            })()}

            <div className="text-sm text-gray-600 mb-4 space-y-1">
              <p><strong>{activeVisit.visitor_name}</strong></p>
              <p className="text-xs">Meeting <strong>{activeVisit.staff_name}</strong></p>
              {activeVisit.purpose && <p className="text-xs">Purpose: {activeVisit.purpose}</p>}
            </div>

            {activeVisit.status === 'inside' && (
              <p className="text-xs text-gray-500 italic" data-testid="visitor-wait-meeting-done">
                Waiting for host to mark the meeting as over. You'll get an EXIT OTP here once they do.
              </p>
            )}

            {(activeVisit.status === 'meeting_done' || activeVisit.status === 'pending_out') && (
              !showOutRemarks && activeVisit.status === 'meeting_done' ? (
                <button
                  onClick={() => setShowOutRemarks(true)}
                  disabled={busy}
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-bold py-3 rounded-xl flex items-center justify-center space-x-2"
                  data-testid="visitor-end-visit-btn"
                >
                  <ArrowRightFromLine size={18} /><span>Press OUT to leave</span>
                </button>
              ) : showOutRemarks ? (
                <div className="space-y-2 text-left bg-purple-50 border-2 border-purple-300 rounded-xl p-3" data-testid="visitor-out-remarks-form">
                  <label className="text-xs font-medium text-purple-900">
                    Remarks (optional, max 50 chars — sent to admin only)
                  </label>
                  <textarea
                    rows={2}
                    maxLength={50}
                    placeholder="Anything to share with admin?"
                    value={outRemarks}
                    onChange={(e) => setOutRemarks(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-purple-200 rounded-lg outline-none focus:border-purple-400 text-sm"
                    data-testid="visitor-out-remarks-input"
                  />
                  <p className="text-xs text-gray-500 text-right">{outRemarks.length}/50</p>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleEndVisit}
                      disabled={busy}
                      className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg"
                      data-testid="visitor-confirm-out-btn"
                    >
                      {busy ? 'Submitting...' : 'Confirm OUT'}
                    </button>
                    <button
                      onClick={() => { setShowOutRemarks(false); setOutRemarks(''); }}
                      className="px-4 py-2.5 bg-gray-200 rounded-lg text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null
            )}

            <button onClick={fetchActive} className="mt-3 text-sm text-gray-500 flex items-center justify-center space-x-1 mx-auto">
              <RefreshCw size={12} /><span>Refresh status</span>
            </button>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-4">© 2026 Carloo Tex</p>
      </div>
    </div>
  );
}
